import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  Chip,
  IconButton,
  CircularProgress,
} from '@mui/material';
import { ContentCopy } from '@mui/icons-material';
import { ApiKey } from '../types';
import { formatTableDate } from '../utils/dateUtils';

interface ApiKeyDialogProps {
  open: boolean;
  onClose: () => void;
  selectedKey: ApiKey | null;
  fullApiKey: string;
  loadingFullKey: boolean;
  onCopyKey: (fullKey: string) => void;
  showCompany?: boolean;
  getCompanyName?: (companyId: string) => string;
}

const ApiKeyDialog: React.FC<ApiKeyDialogProps> = ({
  open,
  onClose,
  selectedKey,
  fullApiKey,
  loadingFullKey,
  onCopyKey,
  showCompany = false,
  getCompanyName,
}) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6">API Key Details</Typography>
          {selectedKey && (
            <Chip 
              label={selectedKey.isActive ? 'Active' : 'Inactive'} 
              size="small"
              color={selectedKey.isActive ? 'success' : 'error'}
            />
          )}
        </Box>
      </DialogTitle>
      <DialogContent>
        {loadingFullKey ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* API Key Info */}
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Name
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                {selectedKey?.name}
              </Typography>
            </Box>

            {selectedKey?.description && (
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Description
                </Typography>
                <Typography variant="body1">
                  {selectedKey.description}
                </Typography>
              </Box>
            )}

            {showCompany && getCompanyName && selectedKey && (
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Company
                </Typography>
                <Typography variant="body1">
                  {getCompanyName(selectedKey.companyId)}
                </Typography>
              </Box>
            )}

            {/* Full API Key */}
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Full API Key
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
                p: 2,
                bgcolor: 'background.paper',
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                fontFamily: 'monospace',
                fontSize: '0.875rem',
                wordBreak: 'break-all'
              }}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontFamily: 'monospace',
                    flex: 1,
                    wordBreak: 'break-all'
                  }}
                >
                  {fullApiKey || 'No key available'}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => onCopyKey(fullApiKey)}
                  sx={{ flexShrink: 0 }}
                >
                  <ContentCopy sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            </Box>

            {/* Additional Info */}
            <Box sx={{ display: 'flex', gap: 3 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Created
                </Typography>
                <Typography variant="body2">
                  {selectedKey?.createdAt
                    ? formatTableDate(selectedKey.createdAt, 'created')
                    : 'N/A'
                  }
                </Typography>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Last Used
                </Typography>
                <Typography variant="body2">
                  {selectedKey?.lastUsedAt
                    ? formatTableDate(selectedKey.lastUsedAt, 'lastUsed')
                    : 'Never'
                  }
                </Typography>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Usage Count
                </Typography>
                <Typography variant="body2">
                  {selectedKey?.usageCount || 0} calls
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ApiKeyDialog; 